import { activities as activitiesData, projects } from "../../../../../db";

/**
 * Shared utility functions for skill calculations across different contexts
 * (Company, Activity, Professional)
 */

export interface SkillData {
  totalWeightedMonths: number;
  totalMonths: number;
}

export interface SkillMaps {
  profession: Map<string, SkillData>;
  lead: Map<string, SkillData>;
  language: Map<string, SkillData>;
  tools: Map<string, SkillData>;
}

export interface NormalizedSkillsByCategory {
  profession: Array<{ name: string; level: number; }>;
  lead: Array<{ name: string; level: number; }>;
  language: Array<{ name: string; level: number; }>;
  tools: Array<{ name: string; level: number; }>;
}

/**
 * Calculate duration in months between two dates
 * Unified implementation to ensure consistency across all calculations
 */
export function calculateDurationInMonths(startDate: string, endDate: string | null): number {
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date();
  
  const yearDiff = end.getFullYear() - start.getFullYear();
  const monthDiff = end.getMonth() - start.getMonth();
  
  return yearDiff * 12 + monthDiff + 1; // +1 to include partial months
}

/**
 * Calculate the actual time intersection between a project and an activity
 * Used for continued projects that span multiple activities
 */
export function calculateProjectActivityIntersection(
  project: { startDate: string; endDate: string | null; activityId: number | number[] | null },
  activity: { id: number; startDate: string; endDate: string | null }
): number {
  // For single-activity projects, return full project duration
  if (!Array.isArray(project.activityId)) {
    return calculateDurationInMonths(project.startDate, project.endDate);
  }

  // For multi-activity projects, calculate time intersection
  const projectStart = new Date(project.startDate);
  const projectEnd = project.endDate ? new Date(project.endDate) : new Date();
  const activityStart = new Date(activity.startDate);
  const activityEnd = activity.endDate ? new Date(activity.endDate) : new Date();

  // Calculate intersection period
  const intersectionStart = projectStart > activityStart ? projectStart : activityStart;
  const intersectionEnd = projectEnd < activityEnd ? projectEnd : activityEnd;

  // If no intersection, return 0
  if (intersectionStart > intersectionEnd) {
    return 0;
  }

  // Calculate months in intersection
  const yearDiff = intersectionEnd.getFullYear() - intersectionStart.getFullYear();
  const monthDiff = intersectionEnd.getMonth() - intersectionStart.getMonth();
  
  return yearDiff * 12 + monthDiff + 1;
}

/**
 * Calculate attributed duration for multi-activity projects in company context
 * Uses time intersection for more accurate attribution
 */
export function calculateCompanyAttributedProjectDuration(
  project: { startDate: string; endDate: string | null; activityId: number | number[] | null },
  companyActivityIds: number[],
  companyActivities: Array<{ id: number; startDate: string; endDate: string | null; }>
): number {
  const totalDuration = calculateDurationInMonths(project.startDate, project.endDate);
  
  if (!Array.isArray(project.activityId)) {
    // Single activity project - return full duration if it belongs to company
    return companyActivityIds.includes(project.activityId as number) ? totalDuration : 0;
  }

  // Multi-activity project: calculate actual time intersection with company activities
  let totalIntersectionMonths = 0;
  
  project.activityId.forEach((actId: number) => {
    if (companyActivityIds.includes(actId)) {
      const activity = companyActivities.find(a => a.id === actId);
      if (activity) {
        const intersection = calculateProjectActivityIntersection(project, activity);
        totalIntersectionMonths += intersection;
      }
    }
  });
  
  // Don't exceed project duration
  return Math.min(totalIntersectionMonths, totalDuration);
}

/**
 * Calculate attributed duration for multi-activity projects in professional context
 * Uses equal split for professional overview calculations
 */
export function calculateProfessionalAttributedProjectDuration(
  project: { startDate: string; endDate: string | null; activityId: number | number[] | null }
): number {
  const totalDuration = calculateDurationInMonths(project.startDate, project.endDate);
  
  // For professional/global calculations, always count full duration
  // Multi-activity projects should contribute their full duration to global totals
  // This avoids artificial deflation of global skill points
  return totalDuration;
}

/**
 * Normalize skill format from legacy to new categorized format
 * Handles both old (skills arrays) and new (categorizedSkills) formats
 */
export function normalizeSkillFormat(project: any): {
  profession: Array<{ name: string; weight: number; }>;
  lead: Array<{ name: string; weight: number; }>;
  language: Array<{ name: string; weight: number; }>;
  tools: Array<{ name: string; weight: number; }>;
} {
  if (project.categorizedSkills) {
    // New format - return as is, with defaults for missing categories
    return {
      profession: project.categorizedSkills.profession || [],
      lead: project.categorizedSkills.lead || [],
      language: project.categorizedSkills.language || [],
      tools: project.categorizedSkills.tools || [],
    };
  }

  // Legacy format - convert to new format
  return {
    profession: project.skills || [],
    lead: [],
    language: [],
    tools: [],
  };
}

/**
 * Create initialized skill maps for all categories
 */
export function createSkillMaps(): SkillMaps {
  return {
    profession: new Map<string, SkillData>(),
    lead: new Map<string, SkillData>(),
    language: new Map<string, SkillData>(),
    tools: new Map<string, SkillData>(),
  };
}

/**
 * Add skill to appropriate skill map
 */
export function addSkillToMap(
  skillMaps: SkillMaps,
  category: keyof SkillMaps,
  skillName: string,
  duration: number,
  weight: number
): void {
  if (!skillMaps[category].has(skillName)) {
    skillMaps[category].set(skillName, { totalWeightedMonths: 0, totalMonths: 0 });
  }
  
  const skillData = skillMaps[category].get(skillName)!;
  skillData.totalWeightedMonths += duration * weight;
  skillData.totalMonths += duration;
}

/**
 * Convert skill maps to normalized skill levels (0-100)
 * Normalizes against total project duration for accurate percentage representation
 */
export function normalizeSkillMaps(
  skillMaps: SkillMaps,
  totalProjectDuration: number
): NormalizedSkillsByCategory {
  const result: NormalizedSkillsByCategory = {
    profession: [],
    lead: [],
    language: [],
    tools: [],
  };

  // Process each category
  (Object.keys(skillMaps) as Array<keyof SkillMaps>).forEach((categoryKey) => {
    const skillMap = skillMaps[categoryKey];
    
    result[categoryKey] = Array.from(skillMap.entries())
      .map(([name, data]) => {
        const normalizedWeight = totalProjectDuration > 0 
          ? (data.totalWeightedMonths / totalProjectDuration) 
          : 0;
        const level = Math.min(100, Math.round(normalizedWeight * 100));
        return { name, level };
      })
      .filter(skill => skill.level > 0)
      .sort((a, b) => b.level - a.level);
  });

  return result;
}

/**
 * Convert normalized skills to legacy format for backward compatibility
 */
export function convertToLegacyFormat(normalizedSkills: NormalizedSkillsByCategory): {
  skills: Array<{ name: string; level: number; }>;
} {
  // Combine profession, lead, language, tools into "skills"
  const skillsSet = new Map<string, number>();
  
  [...normalizedSkills.profession, ...normalizedSkills.lead, ...normalizedSkills.language, ...normalizedSkills.tools].forEach(skill => {
    const existing = skillsSet.get(skill.name) || 0;
    skillsSet.set(skill.name, Math.max(existing, skill.level));
  });

  const skills = Array.from(skillsSet.entries())
    .map(([name, level]) => ({ name, level }))
    .sort((a, b) => b.level - a.level);

  return { skills };
}